import { useCallback, useEffect, useMemo, useState } from "react";
import "./ReportsSectionCompact.css";
import { useNavigate } from "react-router-dom";
import ModalCloseX from "../components/common/ModalCloseX";
import SortableTh from "../components/common/SortableTh";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import {
  buildSuperAdminReportHelpGuide,
  getSuperAdminHelpAnchors,
} from "../constants/superAdminReportHelpGuides";
import useTableSort from "../hooks/useTableSort";
import { useAuth } from "../context/AuthContext";
import {
  exportSuperAdminClientSatisfactionReport,
  getSuperAdminClientSatisfactionFilters,
  postSuperAdminClientSatisfactionReport,
} from "../api/superadminReports";
import {
  clientSatisfactionTooltips,
} from "../constants/clientSatisfactionHelp";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";

const SCOPE_OPTIONS = [
  { value: "all", label: "All Salons" },
  { value: "business", label: "Business" },
  { value: "salon", label: "Salon" },
];

const HELP_GUIDE = buildSuperAdminReportHelpGuide({
  reportKey: "client-satisfaction",
  title: "Client Satisfaction (Super Admin)",
  subtitle:
    "Monitor satisfaction scores and follow-up health across the selected scope.",
  summaryLabel: "Satisfaction Summary",
  detailsLabel: "Satisfaction Details",
  hasExport: true,
});

const HELP_ANCHORS = getSuperAdminHelpAnchors("client-satisfaction");

const DEFAULT_BUCKET_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const SUPERADMIN_TOOLTIPS = {
  scope: "Run this report across all salons, one business, or one salon.",
  business:
    "Required in Business scope. Optional in All Salons scope for narrowing.",
  salon:
    "Required in Salon scope. You can also narrow by salon after choosing a business.",
  show_breakdown:
    "Include Business and Salon columns in the staff breakdown table.",
};

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // fall back to local time
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return `${(num * 100).toFixed(1)}%`;
};

const formatRating = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return num.toFixed(2).replace(/\.?0+$/, "");
};

const formatHours = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return `${num.toFixed(1)} hrs`;
};

const SummaryCard = ({ label, value, hint }) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-2 text-3xl font-semibold text-slate-900">{value}</div>
    {hint ? <div className="mt-1 text-sm text-slate-500">{hint}</div> : null}
  </div>
);

const StatRow = ({ label, value }) => (
  <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
    <div className="font-semibold text-slate-700">{label}</div>
    <div className="text-slate-600">{value}</div>
  </div>
);

export default function SuperAdminClientSatisfaction() {
  const navigate = useNavigate();
  const { is_super_admin, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();

  const [scope, setScope] = useState("all");
  const [businessId, setBusinessId] = useState("");
  const [salonId, setSalonId] = useState("");
  const [showBreakdown, setShowBreakdown] = useState(true);

  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [bucketOptions, setBucketOptions] = useState(DEFAULT_BUCKET_OPTIONS);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState("");

  const businessMap = useMemo(() => {
    const map = new Map();
    (businesses || []).forEach((item) => {
      if (!item) return;
      map.set(String(item.id), item);
    });
    return map;
  }, [businesses]);

  const salonMap = useMemo(() => {
    const map = new Map();
    (salons || []).forEach((item) => {
      if (!item) return;
      map.set(String(item.id), item);
    });
    return map;
  }, [salons]);

  const anchorTimezone = useMemo(() => {
    if (salonId) {
      const salon = salonMap.get(String(salonId));
      if (salon?.timezone) return salon.timezone;
    }
    if (businessId) {
      const business = businessMap.get(String(businessId));
      if (business?.timezone) return business.timezone;
    }
    return authSalonTimezone || "UTC";
  }, [salonId, salonMap, businessId, businessMap, authSalonTimezone]);

  const today = useMemo(
    () => formatDateISO(new Date(), anchorTimezone),
    [anchorTimezone],
  );
  const defaultStart = useMemo(
    () => formatDateISO(addDays(new Date(), -29), anchorTimezone),
    [anchorTimezone],
  );

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [bucket, setBucket] = useState("week");
  const [summary, setSummary] = useState(null);
  const [staffRows, setStaffRows] = useState([]);
  const [trendRows, setTrendRows] = useState([]);
  const [reportMeta, setReportMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const broadScope = scope === "all" || scope === "business";
  const includeEntityColumns = Boolean(reportMeta?.include_entity_columns);

  useEffect(() => {
    setStartDate(defaultStart);
    setEndDate(today);
  }, [defaultStart, today]);

  useEffect(() => {
    if (!broadScope) {
      setShowBreakdown(false);
      return;
    }
    setShowBreakdown(true);
  }, [broadScope]);

  const loadFilters = useCallback(async () => {
    setFiltersLoading(true);
    setFiltersError("");
    try {
      const data = await getSuperAdminClientSatisfactionFilters({
        scope,
        business_id: businessId || undefined,
        salon_id: salonId || undefined,
      });
      setBusinesses(Array.isArray(data?.businesses) ? data.businesses : []);
      setSalons(Array.isArray(data?.salons) ? data.salons : []);
      setBucketOptions(
        Array.isArray(data?.trend_bucket_options) &&
          data.trend_bucket_options.length
          ? data.trend_bucket_options
          : DEFAULT_BUCKET_OPTIONS,
      );
    } catch (err) {
      setFiltersError(err?.message || "Unable to load filters.");
      setBusinesses([]);
      setSalons([]);
    } finally {
      setFiltersLoading(false);
    }
  }, [scope, businessId, salonId]);

  useEffect(() => {
    if (!is_super_admin) return;
    loadFilters();
  }, [is_super_admin, loadFilters]);

  const filteredSalons = useMemo(() => {
    if (!businessId) return salons;
    return (salons || []).filter(
      (item) => String(item.business_id || "") === String(businessId),
    );
  }, [salons, businessId]);

  useEffect(() => {
    if (
      salonId &&
      !filteredSalons.some((item) => String(item.id) === String(salonId))
    ) {
      setSalonId("");
    }
  }, [salonId, filteredSalons]);

  const handleScopeChange = (value) => {
    setScope(value);
    if (value === "all") {
      setSalonId("");
      return;
    }
    if (value === "business") {
      setSalonId("");
    }
  };

  const handleBusinessChange = (value) => {
    setBusinessId(value);
    if (!value && scope !== "salon") {
      setSalonId("");
      return;
    }
    if (salonId) {
      const selectedSalon = salonMap.get(String(salonId));
      if (
        selectedSalon &&
        value &&
        String(selectedSalon.business_id || "") !== String(value)
      ) {
        setSalonId("");
      }
    }
  };

  const handleSalonChange = (value) => {
    setSalonId(value);
    if (!value) return;
    const selectedSalon = salonMap.get(String(value));
    if (selectedSalon?.business_id) {
      setBusinessId(String(selectedSalon.business_id));
    }
  };

  const loadReport = useCallback(async () => {
    if (scope === "business" && !businessId) {
      setError("Choose a business for Business scope.");
      return;
    }
    if (scope === "salon" && !salonId) {
      setError("Choose a salon for Salon scope.");
      return;
    }
    if (!startDate || !endDate) {
      setError("Select a valid start and end date.");
      return;
    }
    if (startDate > endDate) {
      setError("End date can't be before start date.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const report = await postSuperAdminClientSatisfactionReport({
        scope,
        business_id: businessId ? Number(businessId) : undefined,
        salon_id: salonId ? Number(salonId) : undefined,
        start_date: startDate,
        end_date: endDate,
        trend_bucket: bucket,
        show_breakdown: broadScope ? Boolean(showBreakdown) : false,
      });
      setSummary(report?.summary || null);
      setTrendRows(Array.isArray(report?.trends) ? report.trends : []);
      setStaffRows(Array.isArray(report?.staff_rows) ? report.staff_rows : []);
      setReportMeta(report?.meta || null);
    } catch (err) {
      setSummary(null);
      setTrendRows([]);
      setStaffRows([]);
      setReportMeta(null);
      setError(err?.message || "Unable to run Client Satisfaction report.");
    } finally {
      setLoading(false);
    }
  }, [
    scope,
    businessId,
    salonId,
    startDate,
    endDate,
    bucket,
    broadScope,
    showBreakdown,
  ]);

  const handleExport = useCallback(async () => {
    try {
      await exportSuperAdminClientSatisfactionReport({
        scope,
        business_id: businessId ? Number(businessId) : undefined,
        salon_id: salonId ? Number(salonId) : undefined,
        start_date: startDate,
        end_date: endDate,
        trend_bucket: bucket,
        show_breakdown: broadScope ? Boolean(showBreakdown) : false,
      });
    } catch (err) {
      setError(err?.message || "Unable to export CSV.");
    }
  }, [
    scope,
    businessId,
    salonId,
    startDate,
    endDate,
    bucket,
    broadScope,
    showBreakdown,
  ]);

  const allTooltips = useMemo(
    () => ({ ...clientSatisfactionTooltips, ...SUPERADMIN_TOOLTIPS }),
    [],
  );
  const renderLabelWithTooltip = (labelText, tooltipKey, className = "") => (
    <span className={`inline-flex items-center gap-2 ${className}`.trim()}>
      <span>{labelText}</span>
      <InfoTooltip text={allTooltips[tooltipKey]} label={labelText} />
    </span>
  );

  const totalSurveys = summary?.total_surveys ?? 0;

  const distributionRows = useMemo(() => {
    const map = new Map();
    (summary?.distribution || []).forEach((row) => {
      if (row?.rating != null) {
        map.set(Number(row.rating), Number(row.count || 0));
      }
    });
    return [5, 4, 3, 2, 1].map((rating) => ({
      rating,
      count: map.get(rating) || 0,
      percent: totalSurveys ? (map.get(rating) || 0) / totalSurveys : 0,
    }));
  }, [summary?.distribution, totalSurveys]);

  const criteriaRows = useMemo(() => {
    return [
      { label: "Salon", value: formatRating(summary?.avg_salon) },
      { label: "Reception", value: formatRating(summary?.avg_receptionist) },
      { label: "Service", value: formatRating(summary?.avg_service) },
      { label: "Staff", value: formatRating(summary?.avg_staff) },
      {
        label: "Online booking",
        value: formatRating(summary?.avg_online_booking),
      },
    ];
  }, [summary]);

  const chartPoints = useMemo(() => {
    const rows = Array.isArray(trendRows) ? trendRows : [];
    if (rows.length === 0) return { points: "", rows: [] };
    const maxValue = Math.max(
      5,
      ...rows.map((row) => Number(row?.avg_overall || 0)),
    );
    if (rows.length === 1) {
      const y = 20 - (Number(rows[0]?.avg_overall || 0) / maxValue) * 20;
      return { points: `50,${y}`, rows };
    }
    const points = rows
      .map((row, idx) => {
        const x = (idx / (rows.length - 1)) * 100;
        const y = 40 - (Number(row?.avg_overall || 0) / maxValue) * 40;
        return `${x},${y}`;
      })
      .join(" ");
    return { points, rows };
  }, [trendRows]);

  const staffColumns = useMemo(() => {
    const cols = {
      staff_name: { type: "text" },
      survey_count: { type: "number" },
      avg_overall: { type: "number" },
      avg_service: { type: "number" },
      avg_staff: { type: "number" },
      below_four_percent: { type: "number" },
    };
    if (includeEntityColumns) {
      cols.business_name = { type: "text" };
      cols.salon_name = { type: "text" };
    }
    return cols;
  }, [includeEntityColumns]);

  const staffSort = useTableSort({
    rows: staffRows,
    columns: staffColumns,
    defaultSortBy: "survey_count",
  });

  const selectedSalon = salonMap.get(String(salonId));
  const timezoneNote =
    scope === "salon"
      ? `Salon timezone: ${reportMeta?.timezone || selectedSalon?.timezone || anchorTimezone}`
      : "Dates use each salon's local timezone.";

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/superadmin/reports", { replace: true });
  };

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Client Satisfaction
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>

          </div>
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id={HELP_ANCHORS.overview}
              className={`rounded-2xl ${getSectionClassName(HELP_ANCHORS.overview)}`}
            >
              <ReportHelpCard
                title={HELP_GUIDE.title}
                subtitle={HELP_GUIDE.subtitle}
                sections={HELP_GUIDE.sections}
                troubleshooting={HELP_GUIDE.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id={HELP_ANCHORS.filters}
              className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 ${getSectionClassName(
                HELP_ANCHORS.filters
              )}`}
            >
              <div className="flex flex-wrap items-end gap-4">
                <div className="min-w-[180px]">
                  <div
                    className={`${LABEL_TEXT} inline-flex items-center gap-2`}
                  >
                    {renderLabelWithTooltip("Scope", "scope")}
                  </div>
                  <select
                    value={scope}
                    onChange={(event) => handleScopeChange(event.target.value)}
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                    disabled={filtersLoading}
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[220px]">
                  <div
                    className={`${LABEL_TEXT} inline-flex items-center gap-2`}
                  >
                    {renderLabelWithTooltip("Business", "business")}
                  </div>
                  <select
                    value={businessId}
                    onChange={(event) =>
                      handleBusinessChange(event.target.value)
                    }
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                    disabled={filtersLoading}
                  >
                    <option value="">All Businesses</option>
                    {(businesses || []).map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.code} - {business.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[220px]">
                  <div
                    className={`${LABEL_TEXT} inline-flex items-center gap-2`}
                  >
                    {renderLabelWithTooltip("Salon", "salon")}
                  </div>
                  <select
                    value={salonId}
                    onChange={(event) => handleSalonChange(event.target.value)}
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                    disabled={
                      filtersLoading ||
                      !(
                        scope === "salon" ||
                        scope === "business" ||
                        Boolean(businessId)
                      )
                    }
                  >
                    <option value="">
                      {scope === "salon" ? "Select Salon" : "All Salons"}
                    </option>
                    {filteredSalons.map((salonItem) => (
                      <option key={salonItem.id} value={salonItem.id}>
                        {salonItem.name} (ID {salonItem.id})
                      </option>
                    ))}
                  </select>
                </div>
                {broadScope ? (
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={showBreakdown}
                      onChange={(event) =>
                        setShowBreakdown(event.target.checked)
                      }
                    />
                    <span>Show Business/Salon breakdown</span>
                    <InfoTooltip
                      text={allTooltips.show_breakdown}
                      label="Show Business/Salon breakdown"
                    />
                  </label>
                ) : null}
              </div>

              {filtersError ? (
                <div className="text-sm text-rose-600">{filtersError}</div>
              ) : null}

              <div className="flex flex-wrap items-end gap-4">
                <div className="min-w-[180px]">
                  <div
                    className={`${LABEL_TEXT} inline-flex items-center gap-2`}
                  >
                    {renderLabelWithTooltip("Start date", "start_date")}
                  </div>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                  />
                </div>
                <div className="min-w-[180px]">
                  <div
                    className={`${LABEL_TEXT} inline-flex items-center gap-2`}
                  >
                    {renderLabelWithTooltip("End date", "end_date")}
                  </div>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                  />
                </div>
                <div className="min-w-[180px]">
                  <div
                    className={`${LABEL_TEXT} inline-flex items-center gap-2`}
                  >
                    {renderLabelWithTooltip("Trend bucket", "trend_bucket")}
                  </div>
                  <select
                    value={bucket}
                    onChange={(event) => setBucket(event.target.value)}
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                  >
                    {bucketOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end gap-3">
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      className={BTN_PRIMARY}
                      onClick={loadReport}
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Run report"}
                    </button>
                    <InfoTooltip
                      text={allTooltips.run_report}
                      label="Run report"
                    />
                  </div>
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      className={BTN_OUTLINE}
                      onClick={() => {
                        setStartDate(defaultStart);
                        setEndDate(today);
                        setBucket("week");
                      }}
                      disabled={loading}
                    >
                      Reset
                    </button>
                    <InfoTooltip
                      text={allTooltips.reset_filters}
                      label="Reset"
                    />
                  </div>
                </div>
              </div>

              <div className="text-sm text-slate-500">{timezoneNote}</div>
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {error}
              </div>
            )}

            {summary && (
              <div
                id={HELP_ANCHORS.kpis}
                className={`grid gap-4 md:grid-cols-4 ${getSectionClassName(HELP_ANCHORS.kpis)}`}
              >
                <SummaryCard
                  label="Avg overall"
                  value={formatRating(summary.avg_overall)}
                />
                <SummaryCard
                  label="Total surveys"
                  value={summary.total_surveys ?? 0}
                />
                <SummaryCard
                  label="% 4-5"
                  value={formatPercent(summary.four_plus_percent)}
                />
                <SummaryCard
                  label="% below 4"
                  value={formatPercent(summary.below_four_percent)}
                />
              </div>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                  {renderLabelWithTooltip(
                    "Criteria Averages",
                    "criteria_averages",
                  )}
                </div>
                <div className="space-y-2">
                  {criteriaRows.map((row) => (
                    <StatRow
                      key={row.label}
                      label={row.label}
                      value={row.value}
                    />
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                  {renderLabelWithTooltip(
                    "Ratings Distribution",
                    "ratings_distribution",
                  )}
                </div>
                <div className="space-y-2">
                  {distributionRows.map((row) => (
                    <div
                      key={row.rating}
                      className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                    >
                      <div className="font-semibold text-slate-700">
                        {row.rating} stars
                      </div>
                      <div className="text-slate-600">
                        {row.count} ({formatPercent(row.percent)})
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                  {renderLabelWithTooltip(
                    "Follow-up Health",
                    "followup_health",
                  )}
                </div>
                <div className="space-y-2">
                  <StatRow
                    label="Follow-ups created"
                    value={summary?.followups_created ?? 0}
                  />
                  <StatRow
                    label="Follow-ups open"
                    value={summary?.followups_open ?? 0}
                  />
                  <StatRow
                    label="Avg resolution"
                    value={formatHours(summary?.avg_followup_resolution_hours)}
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                  {renderLabelWithTooltip(
                    "Public Review Funnel",
                    "public_review_funnel",
                  )}
                </div>
                <div className="space-y-2">
                  <StatRow
                    label="Eligible"
                    value={summary?.public_review_eligible ?? 0}
                  />
                  <StatRow
                    label="Prompted"
                    value={summary?.public_review_prompted ?? 0}
                  />
                  <StatRow
                    label="Clicked"
                    value={summary?.public_review_clicked ?? 0}
                  />
                  <StatRow
                    label="Click rate"
                    value={formatPercent(summary?.public_review_click_rate)}
                  />
                </div>
              </div>
            </div>

            <div
              id={HELP_ANCHORS.details}
              className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 ${getSectionClassName(
                HELP_ANCHORS.details
              )}`}
            >
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                  {renderLabelWithTooltip(
                    "Satisfaction trend",
                    "satisfaction_trend",
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  Average overall rating by {bucket}.
                </div>
              </div>
              {trendRows.length === 0 ? (
                <div className="text-sm text-slate-500">
                  {loading
                    ? "Loading trend..."
                    : "No trend data for this range."}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <svg
                      viewBox="0 0 100 40"
                      className="w-full h-36 text-rose-500"
                      preserveAspectRatio="none"
                    >
                      <polyline
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        points={chartPoints.points}
                      />
                    </svg>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                    {chartPoints.rows.map((row) => (
                      <div
                        key={row.bucket_label}
                        className="rounded-full border border-slate-200 bg-white px-3 py-1"
                      >
                        {row.bucket_label}: {formatRating(row.avg_overall)} (
                        {row.count})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                    {renderLabelWithTooltip(
                      "Staff Breakdown",
                      "staff_breakdown",
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    Average scores per staff member.
                  </div>
                </div>
                <div
                  id={HELP_ANCHORS.export}
                  className={`inline-flex items-center gap-2 rounded-2xl ${getSectionClassName(
                    HELP_ANCHORS.export
                  )}`}
                >
                  <button
                    type="button"
                    className={BTN_OUTLINE}
                    onClick={handleExport}
                    disabled={!staffRows.length}
                  >
                    Export CSV
                  </button>
                  <InfoTooltip
                    text={allTooltips.export_csv}
                    label="Export CSV"
                  />
                </div>
              </div>

              {loading && staffRows.length === 0 ? (
                <div className="text-sm text-slate-500">Loading staff...</div>
              ) : staffRows.length === 0 ? (
                <div className="text-sm text-slate-500">
                  No staff survey data for this range.
                </div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <table className="w-full table-fixed text-sm">
                    <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        {includeEntityColumns ? (
                          <>
                            <SortableTh
                              label="Business"
                              sortKey="business_name"
                              sortState={staffSort.sortState}
                              onSort={staffSort.handleSort}
                              className="px-4 py-2 text-left"
                            />
                            <SortableTh
                              label="Salon"
                              sortKey="salon_name"
                              sortState={staffSort.sortState}
                              onSort={staffSort.handleSort}
                              className="px-4 py-2 text-left"
                            />
                          </>
                        ) : null}
                        <SortableTh
                          label="Staff"
                          sortKey="staff_name"
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          className="px-4 py-2 text-left"
                        />
                        <SortableTh
                          label="Surveys"
                          sortKey="survey_count"
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          align="right"
                          className="px-4 py-2 text-right"
                        />
                        <SortableTh
                          label="Avg overall"
                          sortKey="avg_overall"
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          align="right"
                          className="px-4 py-2 text-right"
                        />
                        <SortableTh
                          label="Avg service"
                          sortKey="avg_service"
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          align="right"
                          className="px-4 py-2 text-right"
                        />
                        <SortableTh
                          label="Avg staff"
                          sortKey="avg_staff"
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          align="right"
                          className="px-4 py-2 text-right"
                        />
                        <SortableTh
                          label="% below 4"
                          sortKey="below_four_percent"
                          sortState={staffSort.sortState}
                          onSort={staffSort.handleSort}
                          align="right"
                          className="px-4 py-2 text-right"
                        />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {staffSort.sortedRows.map((row, index) => (
                        <tr
                          key={`${row.staff_user_id || "unassigned"}-${row.salon_id || "all"}-${index}`}
                        >
                          {includeEntityColumns ? (
                            <>
                              <td className="px-4 py-3 text-slate-700">
                                {row.business_name || "--"}
                              </td>
                              <td className="px-4 py-3 text-slate-700">
                                {row.salon_name || "--"}
                              </td>
                            </>
                          ) : null}
                          <td className="px-4 py-3 text-slate-900 font-semibold">
                            {row.staff_name || "Unassigned"}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700">
                            {row.survey_count ?? 0}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700">
                            {formatRating(row.avg_overall)}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700">
                            {formatRating(row.avg_service)}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700">
                            {formatRating(row.avg_staff)}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700">
                            {formatPercent(row.below_four_percent)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
